import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Icon } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, TableWrap } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day } from '../lib/format.ts';
import {
  adds,
  AGGS,
  cellText,
  colLabel,
  csvValue,
  emptySpec,
  outLabel,
  PERIODS,
  SOURCE_ICON,
  sourceLabel,
  valueLabel,
  type Agg,
  type Bucket,
  type Chart,
  type Filter,
  type Op,
  type OutCol,
  type Period,
  type Result,
  type SavedReport,
  type Source,
  type SourceCol,
  type Spec,
} from '../lib/reports.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { PinDialog } from './Dashboards.tsx';
import { Bars, downloadCsv } from './Reports.tsx';
import { PivotTable } from '../components/PivotTable.tsx';

const periodLabel = (p: Period) => t(`rpt.p_${p}` as MessageKey);
const opLabel = (o: Op) => t(`rpt.op_${o}` as MessageKey);

/**
 * Reports of your own: lists and totals a business builds itself, without code, from invoices,
 * receipts, bills, customers and the rest; kept for one person or shared with everyone who may
 * see that data.
 */
export function MyReports() {
  const navigate = useNavigate();
  const { data: saved } = useAction<SavedReport[]>('studio.reports');
  const { data: sources } = useAction<Source[]>('studio.report_sources');
  const mine = saved?.filter((r) => r.mine) ?? [];
  const shared = saved?.filter((r) => !r.mine) ?? [];
  const src = (key: string) => sources?.find((s) => s.key === key);

  const card = (r: SavedReport) => {
    const s = src(r.source);
    return (
      <button key={r.id} type="button" className="rpt-card" onClick={() => navigate(`/my-reports/${r.id}`)}>
        <span className="list-icon" aria-hidden="true">
          <Icon name={SOURCE_ICON[r.source] ?? 'chart'} size={18} />
        </span>
        <span className="rpt-card-main">
          <span className="list-title">{r.name}</span>
          <span className="list-sub">
            {[s ? sourceLabel(s) : r.source, r.spec.group?.length ? t('rpt.kind_summary') : t('rpt.kind_list'), periodLabel(r.spec.period)].join(' · ')}
          </span>
        </span>
        {r.shared ? <StatusBadge tone="info" icon="users" label={r.mine ? t('rpt.shared') : (r.by ?? t('rpt.shared'))} /> : null}
        <Icon name="chevronRight" size={18} />
      </button>
    );
  };

  return (
    <Shell>
      <div className="page my-reports">
        <div className="page-head">
          <div>
            <h1>{t('rpt.title')}</h1>
            <p className="muted">{t('rpt.subtitle')}</p>
          </div>
          {sources?.length ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => navigate('/my-reports/new')}>
                {t('rpt.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {!saved || !sources ? (
          <Skeleton h={240} r="var(--radius-lg)" />
        ) : !sources.length ? (
          <EmptyState icon="chart" title={t('rpt.no_sources')} />
        ) : !saved.length ? (
          <EmptyState
            icon="chart"
            title={t('rpt.empty')}
            body={t('rpt.empty_body')}
            action={
              <Button variant="primary" icon="plus" onClick={() => navigate('/my-reports/new')}>
                {t('rpt.new')}
              </Button>
            }
          />
        ) : (
          <>
            {mine.length ? (
              <section>
                <h2 className="section-title">{t('rpt.mine')}</h2>
                <div className="rpt-cards">{mine.map(card)}</div>
              </section>
            ) : null}
            {shared.length ? (
              <section>
                <h2 className="section-title">{t('rpt.shared_with_me')}</h2>
                <div className="rpt-cards">{shared.map(card)}</div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </Shell>
  );
}

/** One saved report: its result for a period, as a table and a chart, and a spreadsheet of it. */
export function MyReport() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { fail } = useUi();
  const { data: saved } = useAction<SavedReport[]>('studio.reports');
  const report = saved?.find((r) => r.id === id);
  const [period, setPeriod] = useState<Period | null>(null);
  const [range, setRange] = useState({ from: '', to: '' });
  const [result, setResult] = useState<Result | null>(null);
  const [pinning, setPinning] = useState(false);
  const [fresh, setFresh] = useState(0);
  const p = period ?? report?.spec.period ?? 'this_month';

  useEffect(() => {
    if (!report) return;
    if (p === 'custom' && (!range.from || !range.to)) return;
    setResult(null);
    action<Result>('studio.run_report', { id: report.id, period: p, ...(p === 'custom' ? range : {}), fresh: fresh > 0 }).then(setResult, (err) => {
      fail(err);
      setResult({ columns: [], rows: [], truncated: false, from: null, to: null });
    });
  }, [report, p, range, fail, fresh]);

  if (saved && !report) {
    return (
      <Shell>
        <div className="page">
          <EmptyState icon="chart" title={t('rpt.gone')} action={<Button onClick={() => navigate('/my-reports')}>{t('rpt.back')}</Button>} />
        </div>
      </Shell>
    );
  }
  const exportCsv = () => {
    if (!result || !report) return;
    downloadCsv(`${report.name}.csv`, result.columns.map(outLabel), result.rows.map((r) => r.map((v, i) => csvValue(result.columns[i]!, v))));
  };

  return (
    <Shell>
      <div className="page my-reports">
        <nav className="prj-crumbs small" aria-label={t('rpt.title')}>
          <Link to="/my-reports">{t('rpt.title')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{report?.name ?? '…'}</span>
        </nav>
        <div className="page-head">
          <div>
            <h1>{report?.name ?? <Skeleton w={220} h={32} />}</h1>
            <p className="muted">{result?.from ? t('rpt.range', { from: day(result.from), to: day(result.to!) }) : result ? t('rpt.all_time') : ' '}</p>
          </div>
          <div className="page-actions">
            <Button icon="download" onClick={exportCsv} disabled={!result?.rows.length}>
              {t('rpt.export')}
            </Button>
            {report ? (
              <Button icon="pushpin" onClick={() => setPinning(true)}>
                {t('dash.pin')}
              </Button>
            ) : null}
            {report?.can_edit ? (
              <Button variant="primary" icon="edit" onClick={() => navigate(`/my-reports/${id}/edit`)}>
                {t('common.edit')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="rpt-bar">
          <PeriodPicker period={p} range={range} onPeriod={setPeriod} onRange={setRange} />
          {result?.computed_at ? <Freshness at={result.computed_at} onRefresh={() => setFresh((n) => n + 1)} /> : null}
        </div>
        {report ? <ResultView spec={report.spec} result={result} /> : <Skeleton h={300} r="var(--radius-lg)" />}
        {pinning && report ? <PinDialog report={report} onClose={() => setPinning(false)} /> : null}
      </div>
    </Shell>
  );
}

/** When the figures were worked out, and a way to work them out again now. */
export function Freshness({ at, onRefresh }: { at: string; onRefresh: () => void }) {
  return (
    <span className="rpt-fresh muted small">
      {t('rpt.updated_at', { time: new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) })}
      <Button size="sm" variant="ghost" icon="repeat" onClick={onRefresh}>
        {t('rpt.refresh')}
      </Button>
    </span>
  );
}

export function PeriodPicker({ period, range, onPeriod, onRange }: { period: Period; range: { from: string; to: string }; onPeriod: (p: Period) => void; onRange: (r: { from: string; to: string }) => void }) {
  return (
    <div className="rpt-period">
      <label className="sr-only" htmlFor="rpt-period">
        {t('rpt.period')}
      </label>
      <select id="rpt-period" className="input" value={period} onChange={(e) => onPeriod(e.target.value as Period)}>
        {PERIODS.map((x) => (
          <option key={x} value={x}>
            {periodLabel(x)}
          </option>
        ))}
      </select>
      {period === 'custom' ? (
        <>
          <input className="input" type="date" aria-label={t('rpt.from')} value={range.from} onChange={(e) => onRange({ ...range, from: e.target.value })} />
          <input className="input" type="date" aria-label={t('rpt.to')} value={range.to} onChange={(e) => onRange({ ...range, to: e.target.value })} />
        </>
      ) : null}
    </div>
  );
}

/** The result: a chart when the report asks for one, the table with its totals, and a note when cut short. */
function ResultView({ spec, result }: { spec: Spec; result: Result | null }) {
  if (!result) return <Skeleton h={300} r="var(--radius-lg)" />;
  if (!result.columns.length) return null;
  if (!result.rows.length) return <EmptyState icon="search" title={t('rpt.nothing')} body={t('rpt.nothing_body')} />;
  const summary = result.columns.some((c) => c.agg);
  if (summary && spec.chart === 'pivot' && spec.group.length === 2) {
    return (
      <>
        <PivotTable result={result} />
        {result.truncated ? <p className="muted small">{t('rpt.truncated')}</p> : null}
      </>
    );
  }
  // A summary's totals come from the database (right for averages too); a list's are added here.
  const firstMeasure = result.columns.findIndex((c) => c.agg);
  const totals = result.columns.map((c, i) =>
    result.totals && c.agg ? (result.totals.grand[i - firstMeasure] as number | null) : i > 0 && adds(c) ? result.rows.reduce((s, r) => s + (Number(r[i]) || 0), 0) : null,
  );
  const showTotals = result.rows.length > 1 && totals.some((x) => x !== null && x !== undefined);
  return (
    <>
      {summary && spec.chart !== 'table' && spec.chart !== 'pivot' ? <ChartView chart={spec.chart} result={result} /> : null}
      <TableWrap className="rpt-table">
        <table className="data-table">
          <thead>
            <tr>
              {result.columns.map((c, i) => (
                <th key={i} scope="col" className={c.type === 'money' || c.type === 'number' || c.agg ? undefined : 'left'}>
                  {outLabel(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((r, n) => (
              <tr key={n}>
                {r.map((v, i) => {
                  const c = result.columns[i]!;
                  return (
                    <td key={i} className={c.type === 'money' || c.type === 'number' || c.agg ? 'tabular' : 'left'}>
                      {cellText(c, v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {showTotals ? (
            <tfoot>
              <tr>
                {result.columns.map((c, i) => (
                  <td key={i} className={i === 0 ? 'left' : 'tabular'}>
                    {i === 0 ? t('rpt.total') : totals[i] === null || totals[i] === undefined ? '' : cellText({ ...c, agg: c.agg === 'count' ? 'count' : undefined }, totals[i])}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </TableWrap>
      {result.truncated ? <p className="muted small">{t('rpt.truncated')}</p> : null}
    </>
  );
}

/** Bars or a line over the first group, for the first thing added up. */
export function ChartView({ chart, result }: { chart: Chart; result: Result }) {
  const g = result.columns[0]!;
  const mIndex = result.columns.findIndex((c) => c.agg);
  const m = result.columns[mIndex]!;
  // With two groups, the chart adds the second one together.
  const map = new Map<string, number>();
  for (const r of result.rows) {
    const k = String(r[0] ?? '');
    map.set(k, (map.get(k) ?? 0) + (Number(r[mIndex]) || 0));
  }
  const items = [...map.entries()].map(([k, v]) => ({ key: k, label: cellText(g, k || null), value: v, tip: `${cellText(g, k || null)}: ${cellText(m, v)}` }));
  if (g.type === 'date') {
    const sorted = [...items].sort((a, b) => a.key.localeCompare(b.key));
    return (
      <div className="card rpt-chart" aria-label={outLabel(m)}>
        <p className="muted small">{outLabel(m)}</p>
        {chart === 'line' ? <LineChart items={sorted} /> : <Bars items={sorted} />}
      </div>
    );
  }
  const top = [...items].sort((a, b) => b.value - a.value).slice(0, 12);
  const max = Math.max(1, ...top.map((i) => Math.abs(i.value)));
  return (
    <div className="card rpt-chart">
      <p className="muted small">{outLabel(m)}</p>
      <ul className="rpt-hbars">
        {top.map((i) => (
          <li key={i.key}>
            <span className="rpt-hbar-label">{i.label}</span>
            <span className="rpt-hbar-track" aria-hidden="true">
              <span style={{ width: `${(Math.abs(i.value) / max) * 100}%` }} />
            </span>
            <span className="rpt-hbar-value tabular">{cellText(m, i.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LineChart({ items }: { items: { key: string; label: string; value: number; tip: string }[] }) {
  const W = 600;
  const H = 200;
  const max = Math.max(1, ...items.map((i) => i.value));
  const min = Math.min(0, ...items.map((i) => i.value));
  const x = (n: number) => (items.length === 1 ? W / 2 : (n / (items.length - 1)) * W);
  const y = (v: number) => H - ((v - min) / (max - min || 1)) * H;
  const points = items.map((i, n) => `${x(n).toFixed(1)},${y(i.value).toFixed(1)}`).join(' ');
  const every = Math.ceil(items.length / 8);
  return (
    <figure className="rpt-line">
      <svg viewBox={`-8 -8 ${W + 16} ${H + 16}`} preserveAspectRatio="none" aria-hidden="true">
        <polyline points={`0,${H} ${points} ${W},${H}`} className="rpt-line-fill" />
        <polyline points={points} className="rpt-line-stroke" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="bars-axis" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((i, n) => (
          <span key={i.key}>{n % every === 0 ? i.label : ''}</span>
        ))}
      </div>
      <table className="sr-only">
        <tbody>
          {items.map((i) => (
            <tr key={i.key}>
              <td>{i.tip}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/* ------------------------------------------------------------------ Builder */

/** Builds a report step by step, with the result shown beside it as it changes. */
export function ReportBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useSession();
  const { data: sources } = useAction<Source[]>('studio.report_sources');
  const { data: saved } = useAction<SavedReport[]>('studio.reports');
  const report = id ? saved?.find((r) => r.id === id) : null;
  const [spec, setSpec] = useState<Spec | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { busy, run } = useSave();

  // Start from the saved report, or from the first source.
  useEffect(() => {
    if (spec || !sources?.length) return;
    if (id && !saved) return;
    setSpec(report ? { ...emptySpec(report.source), ...report.spec } : emptySpec(sources[0]!.key));
  }, [spec, sources, saved, report, id]);

  const source = sources?.find((s) => s.key === spec?.source);
  const key = JSON.stringify(spec);
  useEffect(() => {
    if (!spec || !source) return;
    if (spec.group.length && !spec.measures.length) {
      setResult(null);
      setProblem(t('rpt.need_measure'));
      return;
    }
    if (spec.period === 'custom' && (!spec.from || !spec.to)) return;
    const timer = setTimeout(() => {
      action<Result>('studio.run_report', { spec }).then(
        (r) => (setResult(r), setProblem(null)),
        (err) => setProblem(err instanceof Error ? err.message : t('error.generic')),
      );
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, source]);

  if (!sources || !spec || !source) {
    return (
      <Shell>
        <div className="page">
          <Skeleton h={400} r="var(--radius-lg)" />
        </div>
      </Shell>
    );
  }
  const set = (patch: Partial<Spec>) => setSpec({ ...spec, ...patch });
  const summary = spec.group.length > 0;
  const remove = () =>
    void run(async () => {
      await action('studio.delete_report', { id: report!.id });
      navigate('/my-reports');
    }, t('rpt.deleted'));

  return (
    <Shell>
      <div className="page my-reports">
        <nav className="prj-crumbs small" aria-label={t('rpt.title')}>
          <Link to="/my-reports">{t('rpt.title')}</Link>
          {report ? (
            <>
              <Icon name="chevronRight" size={14} />
              <Link to={`/my-reports/${report.id}`}>{report.name}</Link>
            </>
          ) : null}
          <Icon name="chevronRight" size={14} />
          <span>{report ? t('common.edit') : t('rpt.new')}</span>
        </nav>
        <div className="page-head">
          <div>
            <h1>{report ? t('rpt.edit_title') : t('rpt.new_title')}</h1>
            <p className="muted">{t('rpt.builder_hint')}</p>
          </div>
          <div className="page-actions">
            {report ? (
              <Button variant="ghost" icon="trash" onClick={() => setDeleting(true)}>
                {t('common.delete')}
              </Button>
            ) : null}
            <Button variant="primary" icon="check" onClick={() => setSaving(true)} disabled={!!problem && !result}>
              {t('rpt.save')}
            </Button>
          </div>
        </div>

        <div className="rpt-builder">
          <div className="rpt-steps">
            <section className="rpt-step" aria-labelledby="rpt-s1">
              <h2 id="rpt-s1" className="rpt-step-title">
                <span className="rpt-step-n">1</span> {t('rpt.step_source')}
              </h2>
              <div className="rpt-sources" role="radiogroup" aria-labelledby="rpt-s1">
                {sources.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    role="radio"
                    aria-checked={s.key === spec.source}
                    className={`chip${s.key === spec.source ? ' active' : ''}`}
                    onClick={() => s.key !== spec.source && setSpec({ ...emptySpec(s.key), period: spec.period, chart: spec.chart })}
                  >
                    <Icon name={SOURCE_ICON[s.key] ?? 'chart'} size={16} />
                    {sourceLabel(s)}
                  </button>
                ))}
              </div>
            </section>

            <section className="rpt-step" aria-labelledby="rpt-s2">
              <h2 id="rpt-s2" className="rpt-step-title">
                <span className="rpt-step-n">2</span> {t('rpt.step_shape')}
              </h2>
              <div className="segmented" role="radiogroup" aria-labelledby="rpt-s2">
                {[false, true].map((sum) => (
                  <button
                    key={String(sum)}
                    type="button"
                    role="radio"
                    aria-checked={summary === sum}
                    className={summary === sum ? 'active' : ''}
                    onClick={() => summary !== sum && set(sum ? { group: [firstGroup(source)], measures: [firstMeasure(source)], sort: null } : { group: [], measures: [], sort: null, chart: 'table' })}
                  >
                    <Icon name={sum ? 'chart' : 'list'} size={16} />
                    {sum ? t('rpt.kind_summary') : t('rpt.kind_list')}
                  </button>
                ))}
              </div>
              {summary ? <SummaryEditor source={source} spec={spec} set={set} /> : <ColumnsEditor source={source} spec={spec} set={set} />}
            </section>

            <section className="rpt-step" aria-labelledby="rpt-s3">
              <h2 id="rpt-s3" className="rpt-step-title">
                <span className="rpt-step-n">3</span> {t('rpt.step_which')}
              </h2>
              <Field label={t('rpt.period')} hint={t('rpt.period_hint', { col: colLabel(source.columns.find((c) => c.key === source.date)!) })}>
                <select className="input" value={spec.period} onChange={(e) => set({ period: e.target.value as Period })}>
                  {PERIODS.map((x) => (
                    <option key={x} value={x}>
                      {periodLabel(x)}
                    </option>
                  ))}
                </select>
              </Field>
              {spec.period === 'custom' ? (
                <div className="rpt-two">
                  <Field label={t('rpt.from')}>
                    <input className="input" type="date" value={spec.from ?? ''} onChange={(e) => set({ from: e.target.value || null })} />
                  </Field>
                  <Field label={t('rpt.to')}>
                    <input className="input" type="date" value={spec.to ?? ''} onChange={(e) => set({ to: e.target.value || null })} />
                  </Field>
                </div>
              ) : null}
              <FiltersEditor source={source} filters={spec.filters} onChange={(filters) => set({ filters })} />
            </section>

            <section className="rpt-step" aria-labelledby="rpt-s4">
              <h2 id="rpt-s4" className="rpt-step-title">
                <span className="rpt-step-n">4</span> {t('rpt.step_show')}
              </h2>
              <SortEditor spec={spec} result={result} set={set} />
              {summary ? (
                <div className="field">
                  <span className="field-label" id="rpt-chart">
                    {t('rpt.chart')}
                  </span>
                  <div className="segmented" role="radiogroup" aria-labelledby="rpt-chart">
                    {(['table', 'bar', 'line', ...(spec.group.length === 2 ? ['pivot'] : [])] as Chart[]).map((c) => (
                      <button key={c} type="button" role="radio" aria-checked={spec.chart === c} className={spec.chart === c ? 'active' : ''} onClick={() => set({ chart: c })}>
                        <Icon name={c === 'table' ? 'list' : c === 'bar' ? 'chart' : c === 'pivot' ? 'layout' : 'trendUp'} size={16} />
                        {t(`rpt.chart_${c}` as MessageKey)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          </div>

          <div className="rpt-preview" aria-live="polite">
            <p className="rpt-preview-head muted small">
              <Icon name="eye" size={16} /> {t('rpt.preview')}
            </p>
            {problem ? (
              <div className="callout callout-info" role="note">
                <Icon name="alert" size={16} /> {problem}
              </div>
            ) : (
              <ResultView spec={spec} result={result} />
            )}
          </div>
        </div>
      </div>
      {saving ? (
        <SaveDialog
          report={report ?? null}
          canShare={can('studio.manage')}
          suggested={suggestName(spec, source)}
          busy={busy}
          onClose={() => setSaving(false)}
          onSave={(name, shared) =>
            void run(async () => {
              const r = await action<{ id: string }>('studio.save_report', { ...(report ? { id: report.id } : {}), name, shared, spec });
              navigate(`/my-reports/${r.id}`);
            }, t('rpt.saved', { name }))
          }
        />
      ) : null}
      {deleting && report ? (
        <Confirm title={t('rpt.delete_title', { name: report.name })} body={t('rpt.delete_body')} confirm={t('common.delete')} danger busy={busy} onConfirm={remove} onClose={() => setDeleting(false)} />
      ) : null}
    </Shell>
  );
}

const groupable = (s: Source) => s.columns.filter((c) => c.type !== 'money' && !(c.type === 'number' && !c.custom && c.key !== 'days' && c.key !== 'guests'));
const measurable = (s: Source) => s.columns.filter((c) => c.type === 'money' || c.type === 'number');
function firstGroup(s: Source): string {
  return s.columns.find((c) => c.key === s.date)?.type === 'date' ? `${s.date}@month` : groupable(s)[0]!.key;
}
function firstMeasure(s: Source): { col: string; agg: Agg } {
  const m = measurable(s).find((c) => c.type === 'money') ?? measurable(s)[0];
  return m ? { col: m.key, agg: 'sum' } : { col: s.columns[0]!.key, agg: 'count' };
}
function suggestName(spec: Spec, s: Source): string {
  if (!spec.group.length) return sourceLabel(s);
  const g = s.columns.find((c) => c.key === spec.group[0]!.split('@')[0]);
  return g ? t('rpt.name_by', { what: sourceLabel(s), by: colLabel(g).toLowerCase() }) : sourceLabel(s);
}

function ColumnsEditor({ source, spec, set }: { source: Source; spec: Spec; set: (p: Partial<Spec>) => void }) {
  const chosen = spec.columns.length ? spec.columns : source.columns.slice(0, 6).map((c) => c.key);
  const toggle = (k: string) => set({ columns: chosen.includes(k) ? chosen.filter((x) => x !== k) : [...chosen, k] });
  const move = (k: string, by: number) => {
    const i = chosen.indexOf(k);
    const next = [...chosen];
    next.splice(i, 1);
    next.splice(i + by, 0, k);
    set({ columns: next });
  };
  return (
    <div className="rpt-columns">
      <p className="muted small">{t('rpt.columns_hint')}</p>
      <ol className="rpt-chosen">
        {chosen.map((k, i) => {
          const c = source.columns.find((x) => x.key === k);
          if (!c) return null;
          return (
            <li key={k}>
              <span>{colLabel(c)}</span>
              <IconButton icon="up" label={t('rpt.move_left', { name: colLabel(c) })} disabled={i === 0} onClick={() => move(k, -1)} />
              <IconButton icon="down" label={t('rpt.move_right', { name: colLabel(c) })} disabled={i === chosen.length - 1} onClick={() => move(k, 1)} />
              <IconButton icon="x" label={t('rpt.remove_col', { name: colLabel(c) })} disabled={chosen.length === 1} onClick={() => toggle(k)} />
            </li>
          );
        })}
      </ol>
      <div className="rpt-addcols" aria-label={t('rpt.add_columns')}>
        {source.columns
          .filter((c) => !chosen.includes(c.key))
          .map((c) => (
            <button key={c.key} type="button" className="chip" onClick={() => toggle(c.key)} disabled={chosen.length >= 20}>
              <Icon name="plus" size={14} />
              {colLabel(c)}
            </button>
          ))}
      </div>
    </div>
  );
}

function SummaryEditor({ source, spec, set }: { source: Source; spec: Spec; set: (p: Partial<Spec>) => void }) {
  const groups = groupable(source);
  const measures = measurable(source);
  const setGroup = (i: number, v: string | null) => {
    const next = [...spec.group];
    if (v === null) next.splice(i, 1);
    else next[i] = v;
    set({ group: next, sort: null });
  };
  const groupOptions = groups.flatMap((c) =>
    c.type === 'date' ? (['day', 'month', 'year'] as Bucket[]).map((b) => ({ value: `${c.key}@${b}`, label: `${colLabel(c)} · ${t(`rpt.bucket_${b}` as MessageKey)}` })) : [{ value: c.key, label: colLabel(c) }],
  );
  const measureValue = (m: { col: string; agg: Agg }) => (m.agg === 'count' ? 'count' : `${m.agg}:${m.col}`);
  const measureOptions = [{ value: 'count', label: t('rpt.count') }, ...measures.flatMap((c) => AGGS.map((a) => ({ value: `${a}:${c.key}`, label: t(`rpt.aggopt_${a}` as MessageKey, { col: colLabel(c) }) })))];
  const parseMeasure = (v: string): { col: string; agg: Agg } => (v === 'count' ? { col: source.columns[0]!.key, agg: 'count' } : { agg: v.split(':')[0] as Agg, col: v.slice(v.indexOf(':') + 1) });
  return (
    <div className="rpt-summary">
      {spec.group.map((g, i) => (
        <div key={i} className="rpt-row">
          <Field label={i === 0 ? t('rpt.group_by') : t('rpt.then_by')}>
            <select className="input" value={g} onChange={(e) => setGroup(i, e.target.value)}>
              {groupOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          {i > 0 ? <IconButton icon="x" label={t('rpt.remove_group')} onClick={() => setGroup(i, null)} /> : null}
        </div>
      ))}
      {spec.group.length < 2 ? (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => setGroup(spec.group.length, groupOptions.find((o) => !spec.group.includes(o.value))?.value ?? groupOptions[0]!.value)}>
          {t('rpt.add_group')}
        </Button>
      ) : null}
      {spec.measures.map((m, i) => (
        <div key={i} className="rpt-row">
          <Field label={i === 0 ? t('rpt.add_up') : t('rpt.and')}>
            <select
              className="input"
              value={measureValue(m)}
              onChange={(e) => {
                const next = [...spec.measures];
                next[i] = parseMeasure(e.target.value);
                set({ measures: next, sort: null });
              }}
            >
              {measureOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          {spec.measures.length > 1 ? <IconButton icon="x" label={t('rpt.remove_measure')} onClick={() => set({ measures: spec.measures.filter((_, n) => n !== i), sort: null })} /> : null}
        </div>
      ))}
      {spec.measures.length < 8 ? (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => set({ measures: [...spec.measures, measures[0] ? { col: measures[0].key, agg: 'sum' } : { col: source.columns[0]!.key, agg: 'count' }] })}>
          {t('rpt.add_measure')}
        </Button>
      ) : null}
    </div>
  );
}

const OPS_FOR: Record<SourceCol['type'], Op[]> = {
  text: ['is', 'is_not', 'contains', 'empty', 'not_empty'],
  number: ['is', 'gt', 'lt', 'empty', 'not_empty'],
  money: ['is', 'gt', 'lt'],
  date: ['is', 'gt', 'lt', 'empty', 'not_empty'],
  bool: ['is'],
};

function FiltersEditor({ source, filters, onChange }: { source: Source; filters: Filter[]; onChange: (f: Filter[]) => void }) {
  const setAt = (i: number, f: Filter) => onChange(filters.map((x, n) => (n === i ? f : x)));
  const fresh = (c: SourceCol): Filter => ({ col: c.key, op: c.type === 'text' && !c.values ? 'contains' : 'is', value: c.type === 'bool' ? true : (c.values?.[0] ?? '') });
  return (
    <div className="rpt-filters">
      <span className="field-label">{t('rpt.filters')}</span>
      {filters.length === 0 ? <p className="muted small">{t('rpt.no_filters')}</p> : null}
      {filters.map((f, i) => {
        const c = source.columns.find((x) => x.key === f.col) ?? source.columns[0]!;
        const needsValue = f.op !== 'empty' && f.op !== 'not_empty';
        return (
          <div key={i} className="rpt-filter">
            <select className="input" aria-label={t('rpt.filter_col')} value={c.key} onChange={(e) => setAt(i, fresh(source.columns.find((x) => x.key === e.target.value)!))}>
              {source.columns.map((x) => (
                <option key={x.key} value={x.key}>
                  {colLabel(x)}
                </option>
              ))}
            </select>
            <select className="input" aria-label={t('rpt.filter_op')} value={f.op} onChange={(e) => setAt(i, { ...f, op: e.target.value as Op })}>
              {OPS_FOR[c.type].map((o) => (
                <option key={o} value={o}>
                  {c.type === 'date' && (o === 'gt' || o === 'lt') ? t(`rpt.op_${o}_date` as MessageKey) : opLabel(o)}
                </option>
              ))}
            </select>
            {!needsValue ? (
              <span />
            ) : c.type === 'bool' ? (
              <select className="input" aria-label={t('rpt.filter_value')} value={String(f.value === true)} onChange={(e) => setAt(i, { ...f, value: e.target.value === 'true' })}>
                <option value="true">{t('rpt.yes')}</option>
                <option value="false">{t('rpt.no')}</option>
              </select>
            ) : c.values && f.op !== 'contains' ? (
              <select className="input" aria-label={t('rpt.filter_value')} value={String(f.value ?? '')} onChange={(e) => setAt(i, { ...f, value: e.target.value })}>
                {c.values.map((v) => (
                  <option key={v} value={v}>
                    {valueLabel(c, v)}
                  </option>
                ))}
              </select>
            ) : c.type === 'date' ? (
              <input className="input" type="date" aria-label={t('rpt.filter_value')} value={String(f.value ?? '')} onChange={(e) => setAt(i, { ...f, value: e.target.value })} />
            ) : c.type === 'money' ? (
              <input
                className="input tabular"
                inputMode="decimal"
                aria-label={t('rpt.filter_value')}
                value={f.value === '' || f.value === null || f.value === undefined ? '' : String(Number(f.value) / 100)}
                onChange={(e) => setAt(i, { ...f, value: e.target.value === '' ? '' : Math.round(Number(e.target.value.replace(',', '.')) * 100) || 0 })}
              />
            ) : (
              <input className={`input${c.type === 'number' ? ' tabular' : ''}`} inputMode={c.type === 'number' ? 'decimal' : undefined} aria-label={t('rpt.filter_value')} maxLength={200} value={String(f.value ?? '')} onChange={(e) => setAt(i, { ...f, value: e.target.value })} />
            )}
            <IconButton icon="x" label={t('rpt.remove_filter')} onClick={() => onChange(filters.filter((_, n) => n !== i))} />
          </div>
        );
      })}
      {filters.length < 10 ? (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => onChange([...filters, fresh(source.columns.find((c) => c.values) ?? source.columns[0]!)])}>
          {t('rpt.add_filter')}
        </Button>
      ) : null}
    </div>
  );
}

function SortEditor({ spec, result, set }: { spec: Spec; result: Result | null; set: (p: Partial<Spec>) => void }) {
  const cols: OutCol[] = result?.columns ?? [];
  const current = spec.sort && cols.some((c) => c.key === spec.sort!.key) ? spec.sort : null;
  return (
    <div className="rpt-two">
      <Field label={t('rpt.sort_by')}>
        <select className="input" value={current?.key ?? ''} onChange={(e) => set({ sort: e.target.value ? { key: e.target.value, desc: current?.desc ?? false } : null })}>
          <option value="">{spec.group.length ? t('rpt.sort_groups') : t('rpt.sort_newest')}</option>
          {cols.map((c) => (
            <option key={c.key} value={c.key}>
              {outLabel(c)}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('rpt.order')}>
        <select className="input" value={current?.desc ? 'desc' : 'asc'} disabled={!current} onChange={(e) => set({ sort: { key: current!.key, desc: e.target.value === 'desc' } })}>
          <option value="asc">{t('rpt.asc')}</option>
          <option value="desc">{t('rpt.desc')}</option>
        </select>
      </Field>
    </div>
  );
}

function SaveDialog({ report, canShare, suggested, busy, onClose, onSave }: { report: SavedReport | null; canShare: boolean; suggested: string; busy: boolean; onClose: () => void; onSave: (name: string, shared: boolean) => void }) {
  const [name, setName] = useState(report?.name ?? suggested);
  const [shared, setShared] = useState(report?.shared ?? false);
  const [tried, setTried] = useState(false);
  const error = !name.trim() ? t('form.required') : null;
  const save = () => {
    setTried(true);
    if (!error) onSave(name.trim(), shared);
  };
  const hint = useMemo(() => (shared ? t('rpt.shared_hint') : t('rpt.private_hint')), [shared]);
  return (
    <Dialog
      title={t('rpt.save')}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={save} busy={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('rpt.name')} error={tried ? error : null} required>
        <input className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
      </Field>
      {canShare ? <Switch label={t('rpt.share')} checked={shared} onChange={setShared} /> : null}
      <p className="muted small">{hint}</p>
    </Dialog>
  );
}
